

import { FC, useState, useEffect } from 'react';
import { useSupabase } from '../../hooks/useSupabase';

interface AboutItem {
  id: number;
  title: string;
  description: string;
  image: string;
  icon: string;
  order_index: number;
}

const AboutSection: FC = () => {
  const { supabase } = useSupabase();
  const [items, setItems] = useState<AboutItem[]>([
    {
      id: 1,
      title: '30년 경력의 호텔 셰프',
      description: '30년 동안 요리와 함께 한 시간이 만들어낸 정성스러운 한 그릇. 동양 재료에 대한 안목과 손님들을 위한 진심으로 특별한 맛을 경험할 수 있습니다.',
      image: 'https://images.unsplash.com/photo-1577219491135-ce391730fb2c?w=600&h=400&fit=crop&crop=center',
      icon: 'ri-user-star-line',
      order_index: 1
    },
    {
      id: 2,
      title: '최상급 이베리코 전문점',
      description: '스페인 올리브나무 지역의 최상급 이베리코 흑돼지만을 사용합니다. 이베리코돼지한마리 메뉴로 다양한 부위의 맛있는 맛을 만나보세요.',
      image: 'https://images.unsplash.com/photo-1529692236671-f1f6cf9683ba?w=600&h=400&fit=crop&crop=center',
      icon: 'ri-award-line',
      order_index: 2
    },
    {
      id: 3,
      title: '따뜻한 가마솥밥',
      description: '주문하면 즉석에서 정성스럽게 지어드리는 작은 가마솥밥으로 식사를 완성합니다. 갓 지은 밥의 따뜻함이 특별한 식사 경험을 만들어드립니다.',
      image: 'https://images.unsplash.com/photo-1498654896293-37aacf113fd9?w=600&h=400&fit=crop&crop=center',
      icon: 'ri-fire-line',
      order_index: 3
    },
    {
      id: 4,
      title: '편안한 95석 공간',
      description: '95석의 넓은 공간으로 가족 모임과 회식에 최적화되어 있습니다. 넓은 좌석으로 혼자 식사하기에도 부담 없는 편안한 공간입니다.',
      image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=600&h=400&fit=crop&crop=center',
      icon: 'ri-home-smile-line',
      order_index: 4
    }
  ]);

  // 데이터베이스에서 소개 항목 로드
  useEffect(() => {
    loadItems();
  }, [supabase]);

  const loadItems = async () => {
    if (!supabase) return;
    
    try {
      const { data, error } = await supabase
        .from('about_content')
        .select('*')
        .order('order_index');

      if (error && error.code !== 'PGRST116') {
        console.error('소개 항목 로드 실패:', error);
        return;
      }

      if (data && data.length > 0) {
        setItems(data);
      }
    } catch (error) {
      console.error('소개 항목 로드 중 오류:', error);
    }
  };

  return (
    <section id="about" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            815광복점만의 특별함
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            30년 경력의 셰프가 만드는 정성스러운 한식과 최상급 재료로 특별한 식사 경험을 선사합니다
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {items.map((item, index) => (
            <div
              key={item.id}
              className={`flex flex-col ${
                index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
              } items-center gap-8 bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300`}
            >
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-red-600 to-blue-600 rounded-full flex items-center justify-center">
                    <i className={`${item.icon} text-white text-xl`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800">{item.title}</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </div>
              <div className="flex-1">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-64 object-cover rounded-xl shadow-md"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'https://images.unsplash.com/photo-1544025162-d76694265947?w=600&h=400&fit=crop&crop=center';
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
